/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ComponentProps } from "react"

import { cn } from "@/utils/cn"

import { riskBand } from "../../utils/risk-score"

import styles from "./risk-score.module.css"

/**
 * Returns the class that colours an element by a score's band. The class sets
 * the background and a text colour that stays readable on it, which is why the
 * lighter bands do not simply get white text.
 *
 * The class name is derived from the band, so this function needs no change
 * when the scale gains or loses a band. A device with no score gets no class:
 * `.score` already carries the grey as the fallback of its own `--_bg`.
 *
 * @param score - Risk score, absent when the device has none.
 * @returns The class name for the score's band, or an empty string when there
 *   is no score.
 *
 * @example
 * <span className={clsx("flag", getRiskClassName(flag.score))}>{flag.name}</span>
 */
export function getRiskClassName(score: number | undefined): string {
    const band = riskBand(score)

    return band === "unknown" ? "" : styles[`risk-${band}`]
}

interface Props extends ComponentProps<"div"> {
    /**
     * Risk score of the device, as the API reports it.
     *
     * An absent score reads as `N/A` on the grey unknown band.
     */
    score: number | undefined
}

/**
 * The device's risk score, on the colour of the band it falls in.
 *
 * Remaining props reach the wrapping element, so a caller can place it.
 *
 * @example
 * <RiskScore score={device.riskScore} />
 */
export const RiskScore = ({ className, score, ...props }: Props) => (
    <div {...props} className={cn(styles.score, getRiskClassName(score), className)}>
        <span className="text-xs/none">Risk Score</span>
        <span className="text-3xl/none tabular-nums">{score ?? "N/A"}</span>
    </div>
)
